import { useProjectParams } from '@hooks/useProjectParams';
import { DropdownContext } from 'components/DropdownContext';
import { SelectVersionsWorkOrder } from 'components/SelectVersionsWorkOrder';
import { Toolbar } from 'components/Toolbar';
import { TooltipWrapper } from 'components/TooltipWrapper';
import { WorkOrderConstructionTypesTabSelect } from 'components/WorkOrderConstructionTypesTabSelect';
import { filtersWorkOrderSlice } from 'features/filtersWorkOrder/filtersWorkOrderSlice';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { ContractStatusEnum, ProjectStatusEnum, WorkOrderStatusEnum, WorkOrderVorTypeEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { useProjectData } from 'hooks/useProjectData';
import { useScrollToWithNavigationInfo } from 'hooks/useScrollToWithNavigationInfo';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { cnMixSpace, IconArrowDown, IconWarning, Informer, Tooltip } from 'uikit';
import { destroyAgGridTableFilters, isNumber } from 'utils';
import { getLabelWorkOrder } from 'utils/getLabelWorkOrder';

import type { AgGridReact } from 'ag-grid-react';
import { noun } from 'plural-ru';
import type React from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useContractData } from '../Contract/hooks/useContractData';
import { WorkOrderUseCoefficient } from '../WorkOrderUseCoefficientModal/WorkOrderUseCoefficient';

import { ActsKC2Analytics } from './ActsKC2Analytics/ActsKC2Analytics';
import { AddOthers } from './AddWorkOrderOther';
import { AddWorkOrderUer } from './AddWorkOrderUer';
import { ModalName, useAddItemsToWorkOrderMenu } from './hooks';
import { useConstructionTypes } from './hooks/useConstructionTypes';
import { useSeasons } from './hooks/useSeasons';
import { useWorkOrderObjects } from './hooks/useWorkOrderObjects';
import { useWorkOrderVorTypeAndStatusFollowers } from './hooks/useWorkOrderVorTypeAndStatusFollowers';
import { UploadWorkOrder } from './UploadWorkOrder/UploadWorkOrder';
import { WorkOrderStatusLabels } from './workOrderConstants';
import { WorkOrderFilter } from './WorkOrderFilter';
import type { WorkOrderFilterType } from './WorkOrderFilter/types';
import { WorkOrderFilterLevelEnum } from './WorkOrderFilter/types';
import { WorkOrderKebabMenu } from './WorkOrderKebabMenu';
import { TABLE_SETTINGS_HEIGHT } from './WorkOrderPrice';
import { uerWorkOrderDraftInfo, workOrderVersionsGql } from './workOrderQueries';
import { WorkOrderRequisites } from './WorkOrderRequisites';
import { WorkOrderSkzsPanel } from './WorkOrderSkzsPanel/WorkOrderSkzsPanel';
import { WorkOrderSummaryTable } from './WorkOrderSummaryTableNew';
import { WorkOrderTable } from './WorkOrderTable';
import { useWorkOrderActs } from './WorkOrderTable/hooks';
import { COLUMN_NAMES_WITH_FILTER } from './WorkOrderTable/types';

import './WorkOrder.css';

const WORK_ORDER_PATH_PATTERN = '/projects/:methodology/:id/order/:orderId';

export const WorkOrderNew = () => {
    const { id: projectId, orderId, methodology } = useProjectParams();
    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);
    const [position, setPosition] = useState<number | undefined>();
    const refBtn = useRef<HTMLButtonElement>(null);
    const gridTableRef = useRef<AgGridReact>(null);
    const skzsPanelRefreshRef = useRef<{ uerWorkOrderSKzSInfoRefresh: () => void }>(null);
    const { order: orderRules } = useAccess();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        state,
    }: {
        state: { reload: boolean | number; openEditWorkOrder?: boolean; actNavInfo?: any };
    } = useLocation();

    const { projectData, refreshProject } = useProjectData(projectId);

    const [contractData] = useContractData(projectData?.contractId);

    const catalogUerVersion = contractData?.catalogUerBase?.version;
    const catalogName = contractData?.catalogUerBase?.name || '';

    const getContractData = projectData?.contractNavigation?.data?.contractNavData;
    const statusProject = projectData?.status;

    const isProjectStatusDelayed = projectData?.status === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = projectData?.status === ProjectStatusEnum.Completed;

    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const isContractStatusWork = getContractData?.status === ContractStatusEnum.InImplementation;
    const workOrderId = Number(orderId);

    const [constructionTypeId, setConstructionTypeId] = useState(0);
    const [filters, setFilters] = useState<WorkOrderFilterType>({});

    const [isActsKc2AnalyticsOpen, setIsActsKc2AnalyticsOpen] = useState(false);
    const [actsKc2AnalyticsRootObjectId, setActsKc2AnalyticsRootObjectId] = useState<number>();

    const [constructionTypes, constructionTypesStatus, refreshConstructionTypes] = useConstructionTypes(workOrderId);
    const [workOrderSeasons, , , refreshSeasonsData] = useSeasons(workOrderId, false);
    const { workOrderObjects, patchedObjectForWorkOrderFilter, refreshWorkOrderObjects } =
        useWorkOrderObjects(workOrderId);

    const [workOrderVersionsData, , , workOrderVersionsRequest] = useRequest({
        requestId: 'workOrderVersions',
        query: workOrderVersionsGql,
        withEffect: false,
    });

    const refreshVersionsData = useCallback(() => {
        dispatch(workOrderVersionsRequest({ workOrderId }));
    }, [dispatch, workOrderId, workOrderVersionsRequest]);

    const { workOrderVorTypeData, workOrderVorTypeDataMakeRequest } =
        useWorkOrderVorTypeAndStatusFollowers(workOrderId);

    const vorType = workOrderVorTypeData?.uerWorkOrder?.data?.vorType;
    const isWorkOrderVorTypeXml = vorType === WorkOrderVorTypeEnum.XmlBundle;

    useEffect(() => {
        dispatch(workOrderVersionsRequest({ workOrderId }));
    }, [dispatch, workOrderId, workOrderVersionsRequest]);

    useEffect(() => {
        dispatch(filtersWorkOrderSlice.actions.setFiltersWorkOrder(filters));
    }, [filters, dispatch]);

    useEffect(() => {
        if (state?.openEditWorkOrder) {
            setTimeout(() => refBtn.current?.click(), 500);
        }
    }, [state?.openEditWorkOrder]);

    const isSummaryChapterSelected = filters.level?.value === WorkOrderFilterLevelEnum.TOTALS;

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [projectData?.name, projectData?.uniqueId, methodology, projectId],
    );

    const firstRenderWorkOrderFilterSetter = useCallback(
        ({ savedValuesObj }) => {
            const savedConstructionTypeId = savedValuesObj?.constructionTypeId;
            const savedFilters = savedValuesObj?.[String(savedConstructionTypeId)];

            if (savedConstructionTypeId && !state?.actNavInfo) {
                setConstructionTypeId(savedConstructionTypeId);
            }

            if (!state?.actNavInfo) {
                setFilters(savedFilters ?? {});
            }
        },
        [state?.actNavInfo],
    );

    const firstRenderWorkOrderEmptyFilterSetter = useMemo(() => {
        if (constructionTypes?.length && !state?.actNavInfo) {
            return (setter: (params: { key: string | undefined; newState: any }) => void) => {
                const newConstructionTypeId = constructionTypes[0].id;

                setConstructionTypeId(newConstructionTypeId);
                setter({
                    key: 'constructionTypeId',
                    newState: newConstructionTypeId,
                });
            };
        }

        return null;
    }, [constructionTypes, state?.actNavInfo]);

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: firstRenderWorkOrderFilterSetter,
                withEmptyFilterSetter: firstRenderWorkOrderEmptyFilterSetter,
                matchPathPattern: WORK_ORDER_PATH_PATTERN,
                unableToProcess: constructionTypesStatus === RequestStatus.FETCHING,
            }),
            [constructionTypesStatus, firstRenderWorkOrderEmptyFilterSetter, firstRenderWorkOrderFilterSetter],
        ),
    );

    const handleConstructionTypeChange = useCallback(
        (newConstructionTypeId) => {
            setConstructionTypeId(newConstructionTypeId);
            setFilters(savedFilterState?.[newConstructionTypeId] ?? {});

            handleSaveFilterState({
                key: 'constructionTypeId',
                newState: newConstructionTypeId,
            });
        },
        [handleSaveFilterState, savedFilterState],
    );

    const handleFilterFormChange = useCallback(
        (newFilters: WorkOrderFilterType, justForSaveConstructionTypeId?: number) => {
            let mergedFilters = {};

            setFilters((oldFilters) => {
                mergedFilters = { ...oldFilters, ...newFilters };

                return mergedFilters;
            });

            handleSaveFilterState({
                key: justForSaveConstructionTypeId ? String(justForSaveConstructionTypeId) : String(constructionTypeId),
                newState: mergedFilters,
            });
        },
        [constructionTypeId, handleSaveFilterState],
    );

    const handleRequisitesChanged = useCallback(
        (callVersionsRenew: boolean) => {
            refreshProject();

            workOrderVorTypeDataMakeRequest();

            if (callVersionsRenew) {
                dispatch(workOrderVersionsRequest({ workOrderId }));
            }
        },
        [dispatch, refreshProject, workOrderId, workOrderVersionsRequest, workOrderVorTypeDataMakeRequest],
    );

    const checkedWorkOrderNavs = useMemo(
        () => projectData?.contractNavigation?.data?.workOrderNavs?.find((workOrder) => workOrder.id === workOrderId),
        [projectData?.contractNavigation?.data?.workOrderNavs, workOrderId],
    );

    const titleWorkOrder = useMemo(() => getLabelWorkOrder(checkedWorkOrderNavs), [checkedWorkOrderNavs]);

    const onModalClose = useCallback(
        (newActId?: number) => {
            const navState = {
                reload: Math.random(),
                actNavInfo: {
                    actId: newActId,
                },
            };

            setTimeout(() => navigate('./', { replace: true, state: navState }));
        },
        [navigate],
    );

    const statusWorkOrder = checkedWorkOrderNavs?.status;

    const isNotActKC2 = checkedWorkOrderNavs?.actKC2Navs?.length === 0;
    const isStatusWorkOrderInProgress = statusWorkOrder === WorkOrderStatusEnum.InProgress;
    const isStatusWorkOrderOnFormation = statusWorkOrder === WorkOrderStatusEnum.OnFormation;

    const isActsKc2AnalyticsEnabled = useMemo(
        () =>
            (isStatusWorkOrderInProgress ||
                statusWorkOrder === WorkOrderStatusEnum.Completed ||
                (statusWorkOrder === WorkOrderStatusEnum.Delayed &&
                    workOrderVorTypeData?.uerWorkOrder?.data?.statusFollowers?.includes(
                        WorkOrderStatusEnum.InProgress,
                    ))) &&
            checkedWorkOrderNavs?.metadata?.hasCompletedSkzsActs,
        [
            checkedWorkOrderNavs?.metadata?.hasCompletedSkzsActs,
            isStatusWorkOrderInProgress,
            statusWorkOrder,
            workOrderVorTypeData?.uerWorkOrder?.data?.statusFollowers,
        ],
    );

    const actsKc2AnalyticsButtonTooltip = useMemo(
        () =>
            !checkedWorkOrderNavs?.metadata?.hasCompletedSkzsActs
                ? 'Для отображения диаграммы необходимо наличие согласованного Акта из СКзС'
                : '',
        [checkedWorkOrderNavs?.metadata?.hasCompletedSkzsActs],
    );

    const workOrderHasInProgress = useMemo<boolean>(
        () => checkedWorkOrderNavs?.metadata?.hasInProgress,
        [checkedWorkOrderNavs?.metadata?.hasInProgress],
    );
    const canRollbackWorkOrderChange = useMemo<boolean>(
        () => checkedWorkOrderNavs?.metadata?.canRollbackWorkOrderChange,
        [checkedWorkOrderNavs?.metadata?.canRollbackWorkOrderChange],
    );

    const tableRef = useRef<HTMLDivElement>(null);

    const isWorkOrderRequisitesDisabled = !isContractStatusWork || !orderRules.canEdit;

    const {
        addToWorkOrderMenuItems,
        closeModalsHandler,
        getMenuItemAttributes,
        onMenuMouseMove,
        tooltipPosition,
        onMenuMouseLeave,
        activeTooltipMsg,
        openedModal,
    } = useAddItemsToWorkOrderMenu({
        isDisabledByProjectStatus,
        statusWorkOrder,
        filters,
        statusContract: getContractData?.status,
    });

    const draftVars = useMemo(
        () => ({
            workOrderId,
        }),
        [workOrderId],
    );

    const [draftWorkOrderData, , , draftWorkOrderRequest, resetDraftData] = useRequest({
        requestId: 'draftWorkOrderCounterUer',
        query: uerWorkOrderDraftInfo,
        variables: draftVars,
        withEffect: isStatusWorkOrderOnFormation,
    });

    const refreshDraftData = useCallback(() => {
        if (isStatusWorkOrderOnFormation) {
            dispatch(draftWorkOrderRequest(draftVars));
        }
    }, [dispatch, draftVars, draftWorkOrderRequest, isStatusWorkOrderOnFormation]);

    useEffect(
        () => () => {
            if (!state?.reload) {
                dispatch(resetDraftData(undefined));
            }
        },
        [dispatch, draftVars, draftWorkOrderRequest, isStatusWorkOrderOnFormation, resetDraftData, state?.reload],
    );

    const draftInformer = useMemo(() => {
        const draftCount = draftWorkOrderData?.uerWorkOrderDraftInfo?.data?.draftCount;

        if (draftCount) {
            const msg = `${draftCount} ${noun(draftCount, ...['запись', 'записи', 'записей'])}  в статусе черновик`;

            return (
                <Informer
                    label={msg}
                    status="alert"
                    view="filled"
                    size="s"
                    icon={IconWarning}
                    className="workOrderDraftInformer"
                />
            );
        }

        return null;
    }, [draftWorkOrderData?.uerWorkOrderDraftInfo?.data?.draftCount]);

    const isUploadWorkOrderDisabled = useMemo(
        () =>
            !isWorkOrderVorTypeXml ||
            !isStatusWorkOrderOnFormation ||
            isDisabledByProjectStatus ||
            !isContractStatusWork,
        [isContractStatusWork, isDisabledByProjectStatus, isStatusWorkOrderOnFormation, isWorkOrderVorTypeXml],
    );

    const uploadWorkOrderTooltip = useMemo(() => {
        if (!isWorkOrderVorTypeXml || isStatusWorkOrderInProgress) {
            return !isWorkOrderVorTypeXml
                ? 'Доступно только для ВОР формата xml'
                : 'В статусе "В работе" недоступно добавление новых ВОР';
        }

        return '';
    }, [isStatusWorkOrderInProgress, isWorkOrderVorTypeXml]);

    useEffect(() => {
        if (tableRef.current) {
            setPosition(tableRef.current?.getBoundingClientRect?.()?.top);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableRef, isHeaderOpened, tableRef.current?.getBoundingClientRect?.()?.top]);

    useScrollToWithNavigationInfo({
        constructionTypeSetter: handleConstructionTypeChange,
        objectSetter: handleFilterFormChange,
        objects: workOrderObjects,
        tableApi: gridTableRef?.current,
        isNew: true,
    });

    const reloadWorkOrderTable = useCallback(() => {
        if (gridTableRef?.current) {
            refreshDraftData();
            gridTableRef?.current?.api?.refreshInfiniteCache?.();
            destroyAgGridTableFilters({
                api: gridTableRef?.current?.api,
                columns: Object.keys(COLUMN_NAMES_WITH_FILTER),
            });
        }
    }, [refreshDraftData]);

    const skzsPanelRefresh = useCallback(() => {
        if (skzsPanelRefreshRef?.current) {
            skzsPanelRefreshRef?.current?.uerWorkOrderSKzSInfoRefresh?.();
        }
    }, []);

    const onActsKc2AnalyticsOpen = useCallback(
        (newActsKc2AnalyticsRootObjectId?: number) => {
            setIsActsKc2AnalyticsOpen(true);
            setActsKc2AnalyticsRootObjectId(newActsKc2AnalyticsRootObjectId ?? filters.objectId);
        },
        [filters.objectId],
    );

    const onActsKc2AnalyticsClose = useCallback(() => {
        setIsActsKc2AnalyticsOpen(false);
        setActsKc2AnalyticsRootObjectId(undefined);
    }, []);

    const onUploadWorkOrder = useCallback(() => {
        refreshConstructionTypes();
        refreshWorkOrderObjects();
        refreshSeasonsData();
        reloadWorkOrderTable();
        onModalClose();
    }, [onModalClose, refreshConstructionTypes, refreshSeasonsData, refreshWorkOrderObjects, reloadWorkOrderTable]);

    const { refreshActs } = useWorkOrderActs({
        workOrderId,
        constructionTypeId,
        withEffect: false,
    });

    return (
        <ProjectPageLayout
            title={titleWorkOrder}
            statusWorkOrder={statusWorkOrder}
            breadCrumbsItems={breadCrumbsItems}
            style={{ marginBottom: 0 }}
            headerRightSide={draftInformer}
            isHeaderCollapsable
            layoutEntityKey={workOrderId}
            headerBottom={
                <Toolbar flex fullLine>
                    <WorkOrderRequisites
                        workOrderId={workOrderId}
                        onChange={handleRequisitesChanged}
                        isDisabled={isWorkOrderRequisitesDisabled || isDisabledByProjectStatus}
                        refBtn={refBtn}
                        orderRules={orderRules}
                        projectId={projectId}
                        status={statusWorkOrder}
                    />
                    <UploadWorkOrder
                        workOrderId={workOrderId}
                        onSuccess={onUploadWorkOrder}
                        vorType={vorType}
                        isDisabled={isUploadWorkOrderDisabled}
                        tooltipMessage={uploadWorkOrderTooltip}
                    />
                    {!!workOrderVersionsData?.uerWorkOrderVersions?.length && !isStatusWorkOrderOnFormation && (
                        <SelectVersionsWorkOrder items={workOrderVersionsData?.uerWorkOrderVersions} />
                    )}
                    <WorkOrderKebabMenu
                        workOrderId={workOrderId}
                        contractId={getContractData?.id}
                        statusProject={statusProject}
                        statusContract={getContractData?.status}
                        projectData={projectData}
                        workOrderLabel={titleWorkOrder}
                        refreshProjectData={refreshProject}
                        statusWorkOrder={statusWorkOrder}
                        isDisabledByProjectStatus={isDisabledByProjectStatus}
                        isNotActKC2={isNotActKC2}
                        refreshWorkOrder={() => {
                            onModalClose();
                            reloadWorkOrderTable();
                        }}
                        refreshWorkOrderActs={refreshActs}
                        isWorkOrderVorTypeXml={isWorkOrderVorTypeXml}
                        canRollbackWorkOrderChange={canRollbackWorkOrderChange}
                        refreshVersionsData={refreshVersionsData}
                    />
                    <WorkOrderSkzsPanel
                        projectData={projectData}
                        workOrderNavData={checkedWorkOrderNavs}
                        skzsPanelRefreshRef={skzsPanelRefreshRef}
                    />
                </Toolbar>
            }
        >
            <div
                id="workOrder"
                style={
                    {
                        '--table-settings-height': `${TABLE_SETTINGS_HEIGHT}px`,
                        '--others-height': isNumber(position) && `${position + TABLE_SETTINGS_HEIGHT + 20}px`,
                    } as React.CSSProperties
                }
            >
                {openedModal?.[ModalName.Other] && (
                    <AddOthers
                        workOrderId={workOrderId}
                        constructionTypeId={constructionTypeId}
                        onChange={() => {
                            onModalClose();
                            reloadWorkOrderTable();
                        }}
                        isDisabled={false}
                        workOrderStatus={statusWorkOrder}
                        isOpenedOuter
                        closeOuterHandler={closeModalsHandler}
                        withBtn={false}
                        tooltipPosition={tooltipPosition}
                        workOrderSeasons={workOrderSeasons}
                    />
                )}
                {openedModal?.[ModalName.Uer] && (
                    <AddWorkOrderUer
                        constructionTypeId={constructionTypeId}
                        onClose={closeModalsHandler}
                        workOrderId={workOrderId}
                        contractId={projectData?.contractId}
                        reloadWorkOrderTable={reloadWorkOrderTable}
                        objectId={filters?.objectId}
                        subObjectId={filters?.subObjectId}
                        workOrderStatus={statusWorkOrder}
                        catalogVersion={catalogUerVersion}
                        catalogName={catalogName}
                    />
                )}
                <WorkOrderConstructionTypesTabSelect
                    onChange={handleConstructionTypeChange}
                    constructionTypes={constructionTypes}
                    activeConstructionTypeId={constructionTypeId}
                />
                <div className={cnMixSpace({ mT: 'xl', mB: 'xl' })} style={{ overflow: 'auto', display: 'flex' }}>
                    <WorkOrderFilter
                        workOrderObjects={patchedObjectForWorkOrderFilter}
                        constructionTypeId={constructionTypeId}
                        defaultValues={filters}
                        onChange={handleFilterFormChange}
                        onActsKc2AnalyticsOpen={onActsKc2AnalyticsOpen}
                        isActsKc2AnalyticsEnabled={isActsKc2AnalyticsEnabled}
                        actsKc2AnalyticsButtonTooltip={actsKc2AnalyticsButtonTooltip}
                    />

                    <TooltipWrapper
                        direction="upCenter"
                        message={`Работа с НЗ недоступна в статусе "${
                            statusWorkOrder && WorkOrderStatusLabels[statusWorkOrder]
                        }"`}
                        isHidden={
                            statusWorkOrder === WorkOrderStatusEnum.InProgress ||
                            statusWorkOrder === WorkOrderStatusEnum.OnFormation
                        }
                        style={{ width: '115px', marginLeft: '20px' }}
                        toolTipClassName="toolTipDropdownClassName"
                    >
                        <DropdownContext
                            wrapperClassName={cnMixSpace({ mR: 'xs' })}
                            items={addToWorkOrderMenuItems}
                            btnView="primary"
                            btnSize="s"
                            btnIconSize="s"
                            btnLabel="Добавить"
                            icon={IconArrowDown}
                            getItemAttributesHandler={getMenuItemAttributes}
                            onMouseMoveHandler={onMenuMouseMove}
                            onMouseLeaveHandler={onMenuMouseLeave}
                            isDisabled={
                                !orderRules.canEdit ||
                                statusWorkOrder === WorkOrderStatusEnum.Completed ||
                                statusWorkOrder === WorkOrderStatusEnum.Delayed
                            }
                            className="widthMenu"
                        />

                        {activeTooltipMsg && (
                            <Tooltip
                                size={activeTooltipMsg.length > 80 ? 'm' : 's'}
                                direction="rightCenter"
                                position={tooltipPosition}
                                isInteractive={false}
                                style={{ zIndex: 2 }}
                            >
                                {activeTooltipMsg}
                            </Tooltip>
                        )}
                    </TooltipWrapper>
                </div>

                {openedModal?.[ModalName.Coefficient] && (
                    <WorkOrderUseCoefficient
                        workOrderObjects={patchedObjectForWorkOrderFilter}
                        constructionTypeId={constructionTypeId}
                        closeHandler={closeModalsHandler}
                        updaterApplyRatio={reloadWorkOrderTable}
                        objectId={filters?.objectId}
                    />
                )}

                <div ref={tableRef}>
                    {!isSummaryChapterSelected && (
                        <WorkOrderTable
                            ref={gridTableRef}
                            workOrderId={workOrderId}
                            objectId={filters?.objectId}
                            subObjectId={filters?.subObjectId}
                            constructionTypeId={constructionTypeId}
                            constructionTypes={constructionTypes}
                            workOrderStatus={statusWorkOrder}
                            workOrderHasInProgress={workOrderHasInProgress}
                            costRounding={
                                projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0
                            }
                            isDisabledByProjectStatus={isDisabledByProjectStatus}
                            contractStatus={getContractData?.status}
                            reloadWorkOrderTable={reloadWorkOrderTable}
                            skzsPanelRefresh={skzsPanelRefresh}
                            onActsKc2AnalyticsOpen={onActsKc2AnalyticsOpen}
                            refreshProjectData={refreshProject}
                            catalogVersion={catalogUerVersion}
                            catalogName={catalogName}
                        />
                    )}
                    {isSummaryChapterSelected && (
                        <WorkOrderSummaryTable
                            costRounding={
                                projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding || 0
                            }
                            workOrderSeasons={workOrderSeasons}
                        />
                    )}
                    {isActsKc2AnalyticsOpen && (
                        <ActsKC2Analytics
                            onClose={onActsKc2AnalyticsClose}
                            workOrderObjects={patchedObjectForWorkOrderFilter}
                            constructionTypeId={constructionTypeId}
                            rootObjectId={actsKc2AnalyticsRootObjectId}
                            workOrderId={workOrderId}
                            workOrderSeasons={workOrderSeasons}
                        />
                    )}
                </div>
            </div>
        </ProjectPageLayout>
    );
};

WorkOrderNew.displayName = 'WorkOrder';
